import React from "react";
import styled from "styled-components";
import omit from "lodash/omit";
import { StaticImage, GatsbyImage } from "gatsby-plugin-image";
import { Container, Col, Row, media } from "styled-bootstrap-grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone } from "@fortawesome/free-solid-svg-icons/faPhone";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons/faEnvelope";
import { faHome } from "@fortawesome/free-solid-svg-icons/faHome";
import { faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons/faMapMarkerAlt";
import { YellowButton } from "./DealerForm";
import DealersMap from "./DealersMap";
import { DEALER_DATA, DAYS_OF_WEEK } from "./defaultDealer";
import { processedTime } from "./DealerDefault";
import { Dealer, DatoDealer } from "../../../types";

type DearlerDetailProps = {
  datoDealer: DatoDealer;
  dealer: Dealer;
};

const DetailContainer = styled.section`
  padding: 30px 0;
  background-color: #43525a;
`;

const Heading = styled.h2`
  color: #ffffff;
  line-height: 1em;
  font-size: 28px;
  font-style: italic;
  text-transform: uppercase;
  letter-spacing: 0;
  font-family: "BTBrik-XXLOblique" !important;
  margin: 20px 0;
  ${media.lg`
    font-size: 58px;
  `}
`;

const HeaderUnderline = styled.div`
  border-bottom: 2px;
  border-color: #fec00f;
  border-style: solid;
  width: 100%;
`;

const DetailWrapper = styled.div`
  padding: 50px 0;
  .gatsby-image-wrapper {
    width: 80%;
  }
`;

const DealerContent = styled.div`
  display: flex;
  color: #ffffff;
  font-size: 24px;
  font-style: italic;
  font-weight: 600;
  margin: 10px 0;

  ${media.lg`
    font-size: 32px;
  `}
`;

const StyledExternalLink = styled.a`
  text-decoration: none;
  color: #ffffff;
  font-size: 24px;

  ${media.lg`
    font-size: 32px;
  `}
`;

const UppercaseContent = styled(DealerContent)`
  text-transform: uppercase;
  margin-top: 50px;
  margin-bottom: 30px;
`;

const OpeningScheduleItem = styled.li`
  display: flex;
  color: #fec00f;
  font-size: 24px;
  font-weight: 600;
  margin: 10px 0;
  align-items: center;

  :first-child {
    margin-top: 50px;
  }

  span {
    font-style: italic;
    font-size: 24px;
    margin: 0 12px;
    min-width: 60px;
    color: #ffffff;
    text-transform: capitalize;
  }
`;

const IconCircle = styled.span`
  background-color: #fec00f;
  min-width: 30px;
  height: 30px;
  border-radius: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 20px;
  color: #43525a;
`;

const MapContainer = styled.div`
  width: 100%;
  height: 450px;
  background-color: #ffffff;
  margin-top: 50px;
  margin-bottom: 20px;
`;

const DealerDetail: React.FC<DearlerDetailProps> = ({ dealer, datoDealer }) => {
  const dealerName = datoDealer?.name
    ? (datoDealer.name.includes("-")
        ? datoDealer.name.split("-")[0]
        : datoDealer.name) +
      " " +
      dealer.state
    : (dealer.name.includes("-") ? dealer.name.split("-")[0] : dealer.name) +
      " " +
      dealer.state;

  const enquiryScroll = () => {
    const formEl = document.querySelector("#dealer-form");
    //@ts-ignore
    formEl.scrollIntoView({ behavior: "smooth" });
  };
  let sortedHours: { key: string; value: string }[] = [];

  const openingHour = dealer.hours
    ? omit(dealer.hours, ["location_id", "name"])
    : DEALER_DATA.hours;

  for (let day in DAYS_OF_WEEK) {
    if (isNaN(Number(day))) {
      Object.keys(openingHour).forEach((key) => {
        if (key === day && !sortedHours.includes(openingHour[key])) {
          sortedHours.push({ key, value: openingHour[key] });
        }
      });
    }
  }

  const emailReferrer = () => {
    //@ts-ignore
    if (Array.isArray(window.dataLayer)) {
      //@ts-ignore
      window.dataLayer.push({
        event: "email_dealer_from_website",
        storeId: dealer.id,
        dealerName: dealer.name,
      });
    }
  };

  return (
    <DetailContainer>
      <Container>
        <Row>
          <Col col={12}>
            <Heading>Spitwater {dealer.city} details</Heading>
            <HeaderUnderline />
          </Col>
        </Row>
        <Row>
          <Col col={12} lg={6}>
            <DetailWrapper>
              {datoDealer?.logo ? (
                <GatsbyImage
                  image={datoDealer.logo.gatsbyImageData}
                  alt="product"
                />
              ) : (
                <StaticImage
                  src="../../../images/roylances-logo.png"
                  alt="logo"
                  width={318}
                  placeholder="blurred"
                  layout="fixed"
                />
              )}
              <UppercaseContent>{dealerName}</UppercaseContent>
              <DealerContent>
                <IconCircle>
                  <FontAwesomeIcon icon={faPhone} size="sm" />
                </IconCircle>
                {datoDealer?.phone || dealer.phone || DEALER_DATA.phone}
              </DealerContent>
              <DealerContent onClick={emailReferrer}>
                <IconCircle>
                  <FontAwesomeIcon icon={faEnvelope} size="sm" />
                </IconCircle>
                <StyledExternalLink
                  href={
                    "mailto:" + datoDealer?.email ||
                    dealer.email ||
                    DEALER_DATA.email
                  }
                >
                  {datoDealer?.email || dealer.email || DEALER_DATA.email}
                </StyledExternalLink>
              </DealerContent>
              <DealerContent>
                <IconCircle>
                  <FontAwesomeIcon icon={faHome} size="sm" />
                </IconCircle>
                {datoDealer?.websiteUrl || dealer.url || DEALER_DATA.website}
              </DealerContent>
              <ol>
                {Array.isArray(sortedHours) &&
                  sortedHours.length > 0 &&
                  sortedHours.map((item) => (
                    <OpeningScheduleItem>
                      <span>{item.key}</span>:
                      <span>{processedTime(item.value)}</span>
                    </OpeningScheduleItem>
                  ))}
              </ol>
            </DetailWrapper>
          </Col>
          <Col col={12} lg={6}>
            <MapContainer>
              {datoDealer?.location ? (
                <DealersMap
                  lat={Number(datoDealer.location.latitude)}
                  lng={Number(datoDealer.location.longitude)}
                />
              ) : (
                <DealersMap lat={Number(dealer.lat)} lng={Number(dealer.lng)} />
              )}
            </MapContainer>
            <DealerContent>
              <IconCircle>
                <FontAwesomeIcon icon={faMapMarkerAlt} size="sm" />
              </IconCircle>
              {datoDealer?.address ||
                dealer.display_address ||
                DEALER_DATA.address}
            </DealerContent>
            <YellowButton onClick={() => enquiryScroll()}>
              Enquire now
            </YellowButton>
          </Col>
        </Row>
      </Container>
    </DetailContainer>
  );
};

export default DealerDetail;
